import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/authContext';
import { AppLayout } from '../components/AppLayout';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { FileText, Download, Send, Paperclip, Upload, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { USER_ROLES, PRACTICE_STATUS } from '../lib/constants';

export default function PracticeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [practice, setPractice] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showTechnicalForm, setShowTechnicalForm] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showIntegrationDialog, setShowIntegrationDialog] = useState(false);
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showKODialog, setShowKODialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [integrationNote, setIntegrationNote] = useState('');
  const [reopenNote, setReopenNote] = useState('');
  const [koNote, setKONote] = useState('');
  const [editFormData, setEditFormData] = useState({
    nome_referente: '',
    telefono: '',
    email: '',
    indirizzo: '',
    citta: '',
    tipologia: '',
    coordinate: '',
    grandezza_lotto: '',
    potenza_stimata: 0,
    vincoli_presenti: '',
    note: '',
  });
  const [technicalData, setTechnicalData] = useState({
    analisi_vincolistica: '',
    iter_autorizzativo: '',
    tipologia_struttura: '',
    inverter: '',
    moduli: '',
    connessione: '',
    note_tecniche: '',
  });
  const [uploadingReport, setUploadingReport] = useState(false);

  useEffect(() => {
    loadPractice();
    loadMessages();
  }, [id]);

  const loadPractice = async () => {
    try {
      const response = await api.get(`/api/practices/${id}`);
      setPractice(response.data);
      
      // Load existing technical data if available
      if (response.data.technical_output) {
        setTechnicalData(response.data.technical_output);
      }
    } catch (error) {
      console.error('Failed to load practice:', error);
      toast.error('Errore nel caricamento della pratica');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await api.get(`/api/messages/${id}`);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await api.post('/api/messages', {
        practice_id: id,
        message: newMessage,
      });
      setNewMessage('');
      loadMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Errore nell invio del messaggio');
    } finally {
      setSending(false);
    }
  };

  const handleTakeInCharge = async () => {
    try {
      await api.post('/api/practices/take-in-charge', { practice_id: id });
      toast.success('Pratica presa in carico');
      loadPractice();
    } catch (error) {
      console.error('Failed to take in charge:', error);
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDownload = async (attachmentId, filename) => {
    try {
      const response = await api.get(`/api/files/${attachmentId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error('Errore nel download');
    }
  };

  const handleSaveTechnicalOutput = async () => {
    try {
      await api.post('/api/practices/technical-output', {
        practice_id: id,
        technical_output: technicalData,
      });
      toast.success('Dati tecnici salvati con successo');
      setShowTechnicalForm(false);
      loadPractice();
    } catch (error) {
      console.error('Failed to save technical output:', error);
      toast.error('Errore nel salvataggio');
    }
  };

  const handleUploadReport = async (file) => {
    setUploadingReport(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('practice_id', id);
      formData.append('attachment_type', 'report_tecnico');

      await api.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Report caricato con successo');
      loadPractice();
    } catch (error) {
      console.error('Failed to upload report:', error);
      toast.error('Errore nel caricamento del report');
    } finally {
      setUploadingReport(false);
    }
  };

  const handleCompleteStatus = async (newStatus) => {
    if (newStatus === 'rifiutata') {
      setShowRejectDialog(true);
      return;
    }
    
    try {
      await api.post('/api/practices/update-status', {
        practice_id: id,
        new_status: newStatus,
        note: newStatus === 'completata_in_comitato' ? 'Pratica completata' : '',
      });
      toast.success('Stato pratica aggiornato');
      loadPractice();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleReject = async () => {
    if (!rejectNote.trim()) {
      toast.error('Le note sono obbligatorie per rifiutare una pratica');
      return;
    }

    try {
      await api.post('/api/practices/update-status', {
        practice_id: id,
        new_status: 'rifiutata',
        note: rejectNote,
      });
      toast.success('Pratica rifiutata');
      setShowRejectDialog(false);
      setRejectNote('');
      loadPractice();
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleRequestIntegration = async () => {
    if (!integrationNote.trim()) {
      toast.error('Specifica cosa richiedere per le integrazioni');
      return;
    }

    try {
      await api.post('/api/practices/update-status', {
        practice_id: id,
        new_status: 'da_integrare',
        note: integrationNote,
      });

      // Send message to segnalatore
      await api.post('/api/messages', {
        practice_id: id,
        message: `Richiesta integrazioni:\n${integrationNote}`,
      });

      toast.success('Integrazioni richieste');
      setShowIntegrationDialog(false);
      setIntegrationNote('');
      loadPractice();
      loadMessages();
    } catch (error) {
      console.error('Failed to request integration:', error);
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleReopenPractice = async () => {
    try {
      await api.post('/api/practices/update-status', {
        practice_id: id,
        new_status: 'in_lavorazione',
        note: reopenNote || 'Pratica riaperta',
      });

      // Send message
      if (reopenNote) {
        await api.post('/api/messages', {
          practice_id: id,
          message: `Pratica riaperta:\n${reopenNote}`,
        });
      }

      toast.success('Pratica riaperta');
      setShowReopenDialog(false);
      setReopenNote('');
      loadPractice();
      loadMessages();
    } catch (error) {
      console.error('Failed to reopen practice:', error);
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeletePractice = async () => {
    try {
      await api.delete(`/api/admin/practices/${id}`);
      toast.success('Pratica eliminata con successo');
      setShowDeleteDialog(false);
      navigate('/');
    } catch (error) {
      console.error('Failed to delete practice:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const openEditDialog = () => {
    // Pre-populate form with current practice data
    if (practice?.input_data) {
      setEditFormData({
        nome_referente: practice.input_data.nome_referente || '',
        telefono: practice.input_data.telefono || '',
        email: practice.input_data.email || '',
        indirizzo: practice.input_data.indirizzo || '',
        citta: practice.input_data.citta || '',
        tipologia: practice.input_data.tipologia || 'tetto',
        coordinate: practice.input_data.coordinate || '',
        grandezza_lotto: practice.input_data.grandezza_lotto || '',
        potenza_stimata: practice.input_data.potenza_stimata || 0,
        vincoli_presenti: practice.input_data.vincoli_presenti || '',
        note: practice.input_data.note || '',
      });
    }
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/api/practices/${id}/data`, {
        input_data: editFormData,
      });
      toast.success('Pratica aggiornata con successo');
      setShowEditDialog(false);
      loadPractice();
    } catch (error) {
      console.error('Failed to update practice:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    }
  };

  const handleCompleteIntegration = async () => {
    try {
      await api.post(`/api/practices/${id}/complete-integration`);
      toast.success('Integrazione completata. La pratica è tornata in lavorazione.');
      loadPractice();
      loadMessages();
    } catch (error) {
      console.error('Failed to complete integration:', error);
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleKO = async () => {
    if (!koNote.trim()) {
      toast.error('Inserisci una motivazione per il KO');
      return;
    }
    
    try {
      await api.post('/api/practices/update-status', {
        practice_id: id,
        new_status: 'ko',
        note: koNote,
      });
      toast.success('Pratica segnata come KO');
      setShowKODialog(false);
      setKONote('');
      loadPractice();
      loadMessages();
    } catch (error) {
      console.error('Failed to mark as KO:', error);
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Caricamento...</p>
        </div>
      </AppLayout>
    );
  }

  if (!practice) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Pratica non trovata</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              {practice.input_data.nome_referente}
            </h1>
            <p className="text-muted-foreground">
              {practice.input_data.citta} - {practice.input_data.potenza_stimata} kW
            </p>
          </div>
          <StatusBadge status={practice.status} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dati Referente */}
            <Card>
              <CardHeader>
                <CardTitle>Dati Referente</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <InfoRow label="Nome" value={practice.input_data.nome_referente} />
                <InfoRow label="Telefono" value={practice.input_data.telefono} />
                <InfoRow label="Email" value={practice.input_data.email} />
                <InfoRow label="Indirizzo" value={practice.input_data.indirizzo} />
                <InfoRow label="Città" value={practice.input_data.citta} />
              </CardContent>
            </Card>

            {/* Info Gestione */}
            <Card>
              <CardHeader>
                <CardTitle>Info Gestione</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <InfoRow label="Segnalatore" value={practice.segnalatore_name} />
                {practice.assigned_to_name && (
                  <InfoRow label="Assegnato a" value={practice.assigned_to_name} />
                )}
                <InfoRow 
                  label="Data Creazione" 
                  value={new Date(practice.created_at).toLocaleDateString('it-IT', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })} 
                />
              </CardContent>
            </Card>

            {/* Dati Tecnici */}
            <Card>
              <CardHeader>
                <CardTitle>Dati Tecnici</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <InfoRow
                  label="Tipologia"
                  value={practice.input_data.tipologia === 'tetto' ? 'A Tetto' : 'A Terra'}
                />
                <InfoRow label="Coordinate" value={practice.input_data.coordinate} mono />
                <InfoRow label="Grandezza Lotto" value={practice.input_data.grandezza_lotto} />
                <InfoRow
                  label="Potenza Stimata"
                  value={`${practice.input_data.potenza_stimata} kW`}
                />
                {practice.input_data.vincoli_presenti && (
                  <InfoRow label="Vincoli" value={practice.input_data.vincoli_presenti} />
                )}
                {practice.input_data.note && (
                  <InfoRow label="Note" value={practice.input_data.note} />
                )}
              </CardContent>
            </Card>

            {/* Allegati */}
            <Card>
              <CardHeader>
                <CardTitle>Allegati ({practice.attachments?.length || 0})</CardTitle>
              </CardHeader>
              <CardContent>
                {practice.attachments && practice.attachments.length > 0 ? (
                  <div className="space-y-2">
                    {practice.attachments.map((att) => (
                      <div
                        key={att.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{att.original_filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {(att.size / 1024).toFixed(1)} KB -{' '}
                              {formatDistanceToNow(new Date(att.created_at), {
                                addSuffix: true,
                                locale: it,
                              })}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(att.id, att.original_filename)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nessun allegato</p>
                )}
              </CardContent>
            </Card>

            {/* Output Tecnico */}
            {(practice.technical_output || user?.role === USER_ROLES.UFFICIO_TECNICO || user?.role === USER_ROLES.ADMIN) && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Output Tecnico</CardTitle>
                    {(user?.role === USER_ROLES.UFFICIO_TECNICO || user?.role === USER_ROLES.ADMIN) &&
                      practice.status === PRACTICE_STATUS.IN_LAVORAZIONE &&
                      !showTechnicalForm && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowTechnicalForm(true)}
                        >
                          {practice.technical_output ? 'Modifica' : 'Compila'}
                        </Button>
                      )}
                  </div>
                </CardHeader>
                <CardContent>
                  {showTechnicalForm ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Analisi Vincolistica *</Label>
                        <Textarea
                          value={technicalData.analisi_vincolistica}
                          onChange={(e) =>
                            setTechnicalData({
                              ...technicalData,
                              analisi_vincolistica: e.target.value,
                            })
                          }
                          rows={3}
                          placeholder="Descrivi i vincoli presenti e l'analisi effettuata"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Tipologia Iter Autorizzativo *</Label>
                        <Input
                          value={technicalData.iter_autorizzativo}
                          onChange={(e) =>
                            setTechnicalData({
                              ...technicalData,
                              iter_autorizzativo: e.target.value,
                            })
                          }
                          placeholder="es: PAS, PAUR, DIA, etc."
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Tipologia Struttura *</Label>
                          <Input
                            value={technicalData.tipologia_struttura}
                            onChange={(e) =>
                              setTechnicalData({
                                ...technicalData,
                                tipologia_struttura: e.target.value,
                              })
                            }
                            placeholder="es: Falda, Zavorrato, etc."
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Tipo Connessione</Label>
                          <Input
                            value={technicalData.connessione}
                            onChange={(e) =>
                              setTechnicalData({ ...technicalData, connessione: e.target.value })
                            }
                            placeholder="es: Trifase 400V"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Moduli Fotovoltaici *</Label>
                        <Input
                          value={technicalData.moduli}
                          onChange={(e) =>
                            setTechnicalData({ ...technicalData, moduli: e.target.value })
                          }
                          placeholder="es: Jinko Tiger Neo 550W x 92 pezzi"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Inverter *</Label>
                        <Input
                          value={technicalData.inverter}
                          onChange={(e) =>
                            setTechnicalData({ ...technicalData, inverter: e.target.value })
                          }
                          placeholder="es: SolarEdge SE50K 50kW"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Note Tecniche</Label>
                        <Textarea
                          value={technicalData.note_tecniche}
                          onChange={(e) =>
                            setTechnicalData({ ...technicalData, note_tecniche: e.target.value })
                          }
                          rows={3}
                          placeholder="Eventuali note aggiuntive"
                        />
                      </div>

                      <Separator className="my-4" />

                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Allegati Tecnici Obbligatori</Label>
                        
                        <div className="space-y-2">
                          <Label>Planimetria *</Label>
                          <TechnicalFileUploader
                            attachmentType="planimetria"
                            practiceId={id}
                            onUploadSuccess={loadPractice}
                            disabled={uploadingReport}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Produzione *</Label>
                          <TechnicalFileUploader
                            attachmentType="produzione"
                            practiceId={id}
                            onUploadSuccess={loadPractice}
                            disabled={uploadingReport}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Analisi Vincolistica *</Label>
                          <TechnicalFileUploader
                            attachmentType="analisi_vincolistica"
                            practiceId={id}
                            onUploadSuccess={loadPractice}
                            disabled={uploadingReport}
                          />
                        </div>

                        <p className="text-xs text-muted-foreground">
                          Formati supportati: PDF, DOCX, XLSX, PNG, JPG
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <Button onClick={handleSaveTechnicalOutput} className="flex-1">
                          Salva Dati Tecnici
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowTechnicalForm(false);
                            if (practice.technical_output) {
                              setTechnicalData(practice.technical_output);
                            }
                          }}
                        >
                          Annulla
                        </Button>
                      </div>
                    </div>
                  ) : practice.technical_output ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <InfoRow label="Analisi Vincolistica" value={practice.technical_output.analisi_vincolistica} />
                        <InfoRow label="Iter Autorizzativo" value={practice.technical_output.iter_autorizzativo} />
                        <InfoRow label="Tipologia Struttura" value={practice.technical_output.tipologia_struttura} />
                        <InfoRow label="Moduli" value={practice.technical_output.moduli} />
                        <InfoRow label="Inverter" value={practice.technical_output.inverter} />
                        {practice.technical_output.connessione && (
                          <InfoRow label="Connessione" value={practice.technical_output.connessione} />
                        )}
                        {practice.technical_output.note_tecniche && (
                          <InfoRow label="Note Tecniche" value={practice.technical_output.note_tecniche} />
                        )}
                      </div>

                      <Separator />

                      <div className="space-y-2">
                        <h4 className="font-semibold text-sm">Allegati Tecnici</h4>
                        {practice.attachments && practice.attachments.filter(
                          (att) => ['planimetria', 'produzione', 'analisi_vincolistica'].includes(att.attachment_type)
                        ).length > 0 ? (
                          <div className="space-y-2">
                            {practice.attachments
                              .filter((att) => ['planimetria', 'produzione', 'analisi_vincolistica'].includes(att.attachment_type))
                              .map((att) => (
                                <div
                                  key={att.id}
                                  className="flex items-center justify-between p-2 border rounded-lg hover:bg-muted/50"
                                >
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                      <p className="text-sm font-medium">
                                        {att.attachment_type === 'planimetria' && 'Planimetria'}
                                        {att.attachment_type === 'produzione' && 'Produzione'}
                                        {att.attachment_type === 'analisi_vincolistica' && 'Analisi Vincolistica'}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {att.original_filename} - {(att.size / 1024).toFixed(1)} KB
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDownload(att.id, att.original_filename)}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Nessun allegato tecnico caricato</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Dati tecnici non ancora compilati
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Chat & Actions */}
          <div className="space-y-6">
            {/* Actions */}
            <Card>
              <CardContent className="pt-6 space-y-3">
                {(user?.role === USER_ROLES.UFFICIO_TECNICO || user?.role === USER_ROLES.ADMIN) &&
                  practice.status === PRACTICE_STATUS.IN_ATTESA_PRESA_IN_CARICO && (
                    <Button onClick={handleTakeInCharge} className="w-full">
                      Prendi in Carico
                    </Button>
                  )}

                {(user?.role === USER_ROLES.UFFICIO_TECNICO || user?.role === USER_ROLES.ADMIN) &&
                  practice.status === PRACTICE_STATUS.IN_LAVORAZIONE && (
                    <>
                      <Button
                        onClick={() => handleCompleteStatus('completata_in_comitato')}
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        disabled={!practice.technical_output}
                      >
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Completa Pratica
                      </Button>
                      <Button
                        onClick={() => setShowIntegrationDialog(true)}
                        variant="outline"
                        className="w-full"
                      >
                        <AlertCircle className="mr-2 h-4 w-4" />
                        Richiedi Integrazioni
                      </Button>
                      <Button
                        onClick={() => handleCompleteStatus('rifiutata')}
                        variant="destructive"
                        className="w-full"
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Rifiuta Pratica
                      </Button>
                      {!practice.technical_output && (
                        <p className="text-xs text-muted-foreground text-center">
                          Compila i dati tecnici prima di completare
                        </p>
                      )}
                    </>
                  )}

                {practice.status === PRACTICE_STATUS.COMPLETATA_IN_COMITATO && (
                  <div className="space-y-2">
                    <div className="text-center text-sm text-emerald-600 font-medium">
                      ✓ Pratica Completata
                    </div>
                    {(user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.UFFICIO_TECNICO) && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setShowReopenDialog(true)}
                      >
                        Riapri Pratica
                      </Button>
                    )}
                  </div>
                )}

                {practice.status === PRACTICE_STATUS.RIFIUTATA && (
                  <div className="space-y-2">
                    <div className="text-center text-sm text-rose-600 font-medium">
                      ✗ Pratica Rifiutata
                    </div>
                    {(user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.UFFICIO_TECNICO) && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setShowReopenDialog(true)}
                      >
                        Riapri Pratica
                      </Button>
                    )}
                  </div>
                )}

                {practice.status === PRACTICE_STATUS.DA_INTEGRARE && (
                  <div className="space-y-3">
                    <div className="text-center text-sm text-amber-600 font-medium">
                      ⚠ In Attesa Integrazioni
                    </div>
                    
                    {/* Segnalatore OR Admin/Tecnico can complete integration */}
                    {(user?.role === USER_ROLES.SEGNALATORE || 
                      user?.role === USER_ROLES.ADMIN || 
                      user?.role === USER_ROLES.UFFICIO_TECNICO) && (
                      <>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-xs text-amber-900">
                            {user?.role === USER_ROLES.SEGNALATORE 
                              ? "Carica i documenti richiesti e clicca su 'Integrazione Completata' per inviare nuovamente la pratica all'ufficio tecnico."
                              : "Puoi caricare documenti aggiuntivi e segnare l'integrazione come completata per rimettere la pratica in lavorazione."}
                          </p>
                        </div>
                        <Button
                          onClick={handleCompleteIntegration}
                          className="w-full bg-amber-600 hover:bg-amber-700"
                          data-testid="complete-integration-button"
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Integrazione Completata
                        </Button>
                      </>
                    )}
                    
                    {/* Admin/Tecnico can also reopen */}
                    {(user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.UFFICIO_TECNICO) && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setShowReopenDialog(true)}
                      >
                        Riapri Pratica
                      </Button>
                    )}
                  </div>
                )}

                {/* Admin Delete Button */}
                {user?.role === USER_ROLES.ADMIN && (
                  <>
                    <Separator className="my-4" />
                    {/* Edit Practice Button */}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={openEditDialog}
                      data-testid="edit-practice-button"
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Modifica Pratica
                    </Button>
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => setShowDeleteDialog(true)}
                      data-testid="delete-practice-button"
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Elimina Pratica
                    </Button>
                  </>
                )}

                {/* Tech Office Edit Button */}
                {user?.role === USER_ROLES.UFFICIO_TECNICO && (
                  <>
                    <Separator className="my-4" />
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={openEditDialog}
                      data-testid="edit-practice-button"
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Modifica Pratica
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Chat */}
            <Card className="lg:sticky lg:top-20">
              <CardHeader>
                <CardTitle>Messaggi ({messages.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ScrollArea className="h-96 pr-4">
                  {messages.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Nessun messaggio
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-2xl p-3 ${
                              msg.sender_id === user?.id
                                ? 'bg-primary text-primary-foreground rounded-br-md'
                                : 'bg-muted rounded-bl-md'
                            }`}
                          >
                            <p className="text-xs font-medium mb-1">{msg.sender_name}</p>
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                            <p className="text-xs opacity-70 mt-1">
                              {formatDistanceToNow(new Date(msg.created_at), {
                                addSuffix: true,
                                locale: it,
                              })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>

                <Separator />

                <form onSubmit={handleSendMessage} className="space-y-2">
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Scrivi un messaggio..."
                    rows={3}
                    data-testid="practice-chat-message-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="flex-1"
                      data-testid="practice-chat-send-button"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Invia
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rifiuta Pratica</DialogTitle>
              <DialogDescription>
                Specifica il motivo del rifiuto. Questo campo è obbligatorio.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reject-note">Motivo del rifiuto *</Label>
                <Textarea
                  id="reject-note"
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  placeholder="Descrivi il motivo per cui la pratica viene rifiutata..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowRejectDialog(false);
                setRejectNote('');
              }}>
                Annulla
              </Button>
              <Button variant="destructive" onClick={handleReject} disabled={!rejectNote.trim()}>
                Conferma Rifiuto
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Integration Request Dialog */}
        <Dialog open={showIntegrationDialog} onOpenChange={setShowIntegrationDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Richiedi Integrazioni</DialogTitle>
              <DialogDescription>
                Specifica quali documenti o informazioni aggiuntive sono necessarie.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="integration-note">Integrazioni richieste *</Label>
                <Textarea
                  id="integration-note"
                  value={integrationNote}
                  onChange={(e) => setIntegrationNote(e.target.value)}
                  placeholder="Es: Necessario estratto di mappa aggiornato, foto dettagliate del tetto..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowIntegrationDialog(false);
                setIntegrationNote('');
              }}>
                Annulla
              </Button>
              <Button onClick={handleRequestIntegration} disabled={!integrationNote.trim()}>
                <AlertCircle className="mr-2 h-4 w-4" />
                Richiedi Integrazioni
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reopen Practice Dialog */}
        <Dialog open={showReopenDialog} onOpenChange={setShowReopenDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Riapri Pratica</DialogTitle>
              <DialogDescription>
                La pratica tornerà in stato "In Lavorazione". Puoi aggiungere una nota opzionale.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reopen-note">Motivo riapertura (opzionale)</Label>
                <Textarea
                  id="reopen-note"
                  value={reopenNote}
                  onChange={(e) => setReopenNote(e.target.value)}
                  placeholder="Es: Necessario aggiornamento documentazione, revisione dati tecnici..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowReopenDialog(false);
                setReopenNote('');
              }}>
                Annulla
              </Button>
              <Button onClick={handleReopenPractice} className="bg-blue-600 hover:bg-blue-700">
                Riapri Pratica
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Practice Dialog */}
        {practice && (
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent data-testid="delete-practice-dialog">
              <DialogHeader>
                <DialogTitle>Elimina Pratica</DialogTitle>
                <DialogDescription>
                  Sei sicuro di voler eliminare questa pratica? Questa azione non può essere annullata.
                  La pratica e tutti i suoi dati verranno rimossi dal sistema.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-amber-900">
                    ⚠️ Attenzione: Questa azione è permanente
                  </p>
                  <p className="text-sm text-amber-700 mt-2">
                    Cliente: <strong>{practice.input_data.nome_referente}</strong><br />
                    Città: {practice.input_data.citta}<br />
                    Potenza: {practice.input_data.potenza_stimata} kW
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteDialog(false)}
                  data-testid="delete-cancel-button"
                >
                  Annulla
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDeletePractice}
                  data-testid="delete-confirm-button"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Elimina Definitivamente
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Edit Practice Dialog */}
        {showEditDialog && (
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Modifica Pratica</DialogTitle>
                <DialogDescription>
                  Aggiorna i dati del referente e i dati tecnici della pratica
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6 py-4">
                {/* Dati Referente */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Dati Referente</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-nome">Nome Referente</Label>
                      <Input
                        id="edit-nome"
                        value={editFormData.nome_referente}
                        onChange={(e) => setEditFormData({...editFormData, nome_referente: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-telefono">Telefono</Label>
                      <Input
                        id="edit-telefono"
                        value={editFormData.telefono}
                        onChange={(e) => setEditFormData({...editFormData, telefono: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email">Email</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-indirizzo">Indirizzo</Label>
                    <Input
                      id="edit-indirizzo"
                      value={editFormData.indirizzo}
                      onChange={(e) => setEditFormData({...editFormData, indirizzo: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-citta">Città</Label>
                    <Input
                      id="edit-citta"
                      value={editFormData.citta}
                      onChange={(e) => setEditFormData({...editFormData, citta: e.target.value})}
                    />
                  </div>
                </div>

                {/* Dati Tecnici */}
                <Separator />
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Dati Tecnici</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-tipologia">Tipologia</Label>
                      <select
                        id="edit-tipologia"
                        value={editFormData.tipologia}
                        onChange={(e) => setEditFormData({...editFormData, tipologia: e.target.value})}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                      >
                        <option value="tetto">Tetto</option>
                        <option value="terra">Terra</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-potenza">Potenza Stimata (kW)</Label>
                      <Input
                        id="edit-potenza"
                        type="number"
                        step="0.1"
                        value={editFormData.potenza_stimata}
                        onChange={(e) => setEditFormData({...editFormData, potenza_stimata: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-coordinate">Coordinate (lat, lng)</Label>
                    <Input
                      id="edit-coordinate"
                      value={editFormData.coordinate}
                      onChange={(e) => setEditFormData({...editFormData, coordinate: e.target.value})}
                      placeholder="es: 40.8518, 14.2681"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-grandezza">Grandezza Lotto</Label>
                    <Input
                      id="edit-grandezza"
                      value={editFormData.grandezza_lotto}
                      onChange={(e) => setEditFormData({...editFormData, grandezza_lotto: e.target.value})}
                      placeholder="es: 1000 mq"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-vincoli">Vincoli Presenti</Label>
                    <Textarea
                      id="edit-vincoli"
                      value={editFormData.vincoli_presenti}
                      onChange={(e) => setEditFormData({...editFormData, vincoli_presenti: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-note">Note</Label>
                    <Textarea
                      id="edit-note"
                      value={editFormData.note}
                      onChange={(e) => setEditFormData({...editFormData, note: e.target.value})}
                      rows={3}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                  Annulla
                </Button>
                <Button onClick={handleSaveEdit} data-testid="save-edit-button">
                  Salva Modifiche
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </AppLayout>
  );
}

const InfoRow = ({ label, value, mono }) => (
  <div className="flex justify-between py-1">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`text-sm font-medium ${mono ? 'font-mono' : ''}`}>{value}</span>
  </div>
);

const TechnicalFileUploader = ({ attachmentType, practiceId, onUploadSuccess, disabled }) => {
  const [uploading, setUploading] = React.useState(false);
  const [uploadedFile, setUploadedFile] = React.useState(null);

  React.useEffect(() => {
    // Check if file already exists
    api.get(`/api/practices/${practiceId}`)
      .then(response => {
        const existing = response.data.attachments?.find(
          att => att.attachment_type === attachmentType
        );
        if (existing) {
          setUploadedFile(existing);
        }
      })
      .catch(console.error);
  }, [practiceId, attachmentType]);

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('practice_id', practiceId);
      formData.append('attachment_type', attachmentType);

      const response = await api.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('File caricato con successo');
      setUploadedFile({
        id: response.data.attachment_id,
        original_filename: response.data.filename,
        size: response.data.size,
      });
      if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  if (uploadedFile) {
    return (
      <div className="flex items-center justify-between p-3 border rounded-lg bg-emerald-50 border-emerald-200">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-emerald-600" />
          <div>
            <p className="text-sm font-medium text-emerald-900">{uploadedFile.original_filename}</p>
            <p className="text-xs text-emerald-700">
              {(uploadedFile.size / 1024).toFixed(1)} KB - Caricato ✓
            </p>
          </div>
        </div>
        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="border-2 border-dashed rounded-lg p-4 hover:bg-muted/50 transition-colors">
      <label className="flex items-center justify-center gap-2 cursor-pointer">
        <Upload className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          {uploading ? 'Caricamento...' : 'Seleziona file'}
        </span>
        <input
          type="file"
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              handleUpload(e.target.files[0]);
            }
          }}
          className="hidden"
          disabled={disabled || uploading}
          accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
        />
      </label>
    </div>
  );
};